import React, { memo } from "react";
import { Platform } from "react-native";

import ListPlayerCompare from "@/features/compare-players/components/organisms/ListPlayerCompare";

import { Box } from "@/components/atoms/Box";
import { Divider } from "@/components/atoms/Divider";
import {
  Popover,
  PopoverBackdrop,
  PopoverContent,
} from "@/components/atoms/Popover";
import { PlayerCardData } from "@/types/playerCardTypes";

// design-system: dynamic-style waiver — measured popover max-height (60% of
// screen); no closed sizing token expresses it. Passed as a single style ref.
const maxHeightStyle = (maxHeight: number) => ({ maxHeight });

/**
 * Props for DropDownPlayerView
 */
interface Props {
  isOpen: boolean;
  loadingWatchList: boolean;
  loadingMyRoster: boolean;
  watchListName: string;
  selectedComparisonPlayerLabel: string;
  listWatchlistPlayers?: PlayerCardData[];
  listMyRosterPlayers?: PlayerCardData[];
  onOpen: () => void;
  onClose: () => void;
  onSelectComparePlayer: (player: PlayerCardData) => void;
  onLoadMore: (value: string) => void;
  isDark: boolean;
  renderTrigger: (triggerProps: Record<string, unknown>) => React.ReactNode;
  popoverMaxHeight: number;
}

/**
 * View component for player selection dropdown popover
 * Renders a popover with bounded height and scrollable lists of watchlist and roster players.
 * The bounded maxHeight prevents FlashList overlap on native modals where flex-1 has no
 * deterministic parent height.
 * @param root0 - Component props
 * @param root0.isOpen - Whether the dropdown popover is currently open
 * @param root0.watchListName - Name of the selected watchlist to display
 * @param root0.loadingMyRoster - Loading state for roster player list
 * @param root0.loadingWatchList - Loading state for watchlist player list
 * @param root0.listMyRosterPlayers - Array of players from user's roster
 * @param root0.listWatchlistPlayers - Array of players from selected watchlist
 * @param root0.onClose - Callback to close the dropdown popover
 * @param root0.onLoadMore - Callback to load more players when scrolling to end
 * @param root0.onSelectComparePlayer - Callback when a player is selected
 * @param root0.isDark - Whether dark theme is active (for styling)
 * @param root0.renderTrigger - Memoized function to render the trigger element
 * @param root0.popoverMaxHeight - Bounded pixel height for popover content (60% of screen)
 */
const DropDownPlayerView = ({
  isOpen,
  watchListName,
  loadingMyRoster,
  loadingWatchList,
  listMyRosterPlayers,
  listWatchlistPlayers,
  onClose,
  onLoadMore,
  onSelectComparePlayer,
  isDark,
  renderTrigger,
  popoverMaxHeight,
}: Props) => (
  <Popover
    isOpen={isOpen}
    onClose={onClose}
    placement="bottom right"
    offset={8}
    crossOffset={0}
    size="lg"
    trigger={renderTrigger}
    useRNModal={Platform.OS !== "web"}
  >
    <PopoverBackdrop onPress={onClose} />
    <PopoverContent
      className={`w-64 flex-1 p-0 text-left ${isDark ? "border-0 bg-surface-action" : "bg-surface-raised"}`}
      // eslint-disable-next-line no-restricted-syntax -- measured popover max-height (60% screen, runtime); PopoverContent is a bare re-export atom with no UNSAFE_style prop, style= passes through
      style={maxHeightStyle(popoverMaxHeight)}
    >
      {!!listWatchlistPlayers && (
        <Box className="max-h-1/2 min-h-20 gap-4 pb-4">
          <ListPlayerCompare
            data={listWatchlistPlayers}
            title={watchListName}
            loading={loadingWatchList}
            keyList="watchlist"
            onSelectComparePlayer={onSelectComparePlayer}
            onLoadMore={onLoadMore}
          />
          <Divider
            className={`h-2 ${isDark ? "bg-outline-emphasis" : "bg-surface-muted"}`}
          />
        </Box>
      )}
      <Box className="flex-1">
        <ListPlayerCompare
          data={listMyRosterPlayers}
          title="My Roster"
          loading={loadingMyRoster}
          keyList="myroster"
          onSelectComparePlayer={onSelectComparePlayer}
          onLoadMore={onLoadMore}
        />
      </Box>
    </PopoverContent>
  </Popover>
);

DropDownPlayerView.displayName = "DropDownPlayerView";

export default memo(DropDownPlayerView);
